import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import { useFloatingAIChat } from '../store';

/**
 * Where the user was when they handed off to the scheduling agent.
 *
 * Distinct from `SchedulingEntryPoint` in the CRM store, which attributes the scheduling FORM.
 * This one attributes the AGENT, and it is the property that answers "where do AI schedulings come
 * from" — before it existed, every entry point emitted the same event with no way to tell them
 * apart, and two of them emitted nothing at all.
 */
export type SchedulingAgentEntryPoint =
  | 'fab'
  | 'nudge'
  | 'beacon'
  | 'beacon-unassociated'
  | 'upcoming-tickets'
  | 'order-timeline'
  | 'order-summary'
  | 'orders-calendar'
  | 'orders-calendar-next-available'
  | 'orders-calendar-uninstallations'
  | 'onboarding';

/**
 * The single entry point for handing the user over to the scheduling agent.
 *
 * Every surface that offers scheduling goes through here, so the prompts have one owner instead of
 * living as string literals in each caller — the ticket prompt alone was copy-pasted in three
 * components.
 *
 * It emits the canonical `AI - Floating Chat - Opened` with the `entryPoint`. Callers keep their
 * own domain events (a beacon click and a calendar click mean different things); this one exists so
 * the agent funnel has a single, attributable source.
 */
export const useSchedulingAgent = () => {
  const open = useFloatingAIChat((s) => s.open);
  // The prompt lands in the chat as the user's OWN opening message, so it follows the user's locale
  // — hardcoded Portuguese showed an en-US user a message they never wrote.
  const { t } = useTranslation('ai');

  const handoff = (prompt: string, entryPoint: SchedulingAgentEntryPoint, extra?: Record<string, unknown>) => {
    Analytics.track('AI - Floating Chat - Opened', { ...extra, component: 'scheduling-agent', journeyId: 'installation', entryPoint, prompt });
    open(prompt);
  };

  return {
    /** The fleet has one specific pending ticket — the agent starts already scoped to it. */
    openForTicket: (ticketId: string, entryPoint: SchedulingAgentEntryPoint) =>
      handoff(t('floating_chat_prompts.installation_ticket', { ticketId }), entryPoint, { ticketId, scoped: true }),
    /** Several (or unspecified) pending installations — the agent starts from the fleet. */
    openForPendingInstalls: (entryPoint: SchedulingAgentEntryPoint) => handoff(t('floating_chat_prompts.installation'), entryPoint, { scoped: false }),
    /** Any other scheduling conversation (a date, uninstallations, an unassociated-device scenario). */
    openWithPrompt: (prompt: string, entryPoint: SchedulingAgentEntryPoint) => handoff(prompt, entryPoint, { scoped: false }),
  };
};

export default useSchedulingAgent;
